import {
  Boxes,
  Braces,
  Cloud,
  CloudCog,
  Code2,
  Cog,
  DatabaseZap,
  GitBranch,
  Gauge,
  Layers3,
  MapPinned,
  Network,
  PlugZap,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
  Smartphone,
  Workflow,
} from 'lucide-react';
import type { NavItem, Product, Service, ServiceDetail, SolutionDetail } from '../types/site';

export const navItems: NavItem[] = [
  { label: 'Services', href: '/services' },
  { label: 'Solutions', href: '/solutions' },
  { label: 'Products', href: '/products' },
  { label: 'About', href: '/about' },
];

export const services: Service[] = [
  {
    title: 'Custom software development',
    description:
      'Purpose-built systems designed around the business processes that make your operation unique.',
    icon: Code2,
    accent: 'crimson',
  },
  {
    title: 'Web platforms',
    description:
      'Modern, responsive applications and operational portals that make important work easier to run.',
    icon: Layers3,
    accent: 'blue',
  },
  {
    title: 'Mobile applications',
    description:
      'Practical mobile tools for customers, drivers, employees, and field teams wherever work happens.',
    icon: Smartphone,
    accent: 'violet',
  },
  {
    title: 'Integrations & APIs',
    description:
      'Connect software, devices, and third-party platforms so information can move without manual re-entry.',
    icon: PlugZap,
    accent: 'blue',
  },
  {
    title: 'Automation',
    description:
      'Eliminate repetitive workflows and manual processing with rules, triggers, and useful system feedback.',
    icon: Workflow,
    accent: 'crimson',
  },
  {
    title: 'SaaS product development',
    description:
      'Take a product idea from concept through MVP and into a dependable production platform.',
    icon: Cloud,
    accent: 'violet',
  },
];

export const serviceDetails: ServiceDetail[] = [
  {
    slug: 'custom-software',
    number: '01',
    title: 'Custom software development',
    summary:
      'Bespoke software for businesses with processes that generic tools cannot effectively support.',
    description:
      'We turn the operational knowledge in your business into software that is clearer to use, easier to change, and better aligned with how work actually gets done. That might mean replacing a patchwork of tools, extending a legacy system, or creating an entirely new operational layer.',
    capabilities: [
      'Operational platforms',
      'Internal systems',
      'Customer portals',
      'Administration systems',
      'Workflow tools',
      'Reporting platforms',
      'Legacy modernisation',
    ],
    icon: Code2,
    accent: 'crimson',
  },
  {
    slug: 'web-applications',
    number: '02',
    title: 'Web application development',
    summary: 'Responsive web applications that make complex work easier to manage from anywhere.',
    description:
      'We design and build web applications that feel dependable in the moments your teams rely on them most. From dashboards and administration systems to customer-facing portals and real-time interfaces, we focus on the workflow first and the technology second.',
    capabilities: [
      'React applications',
      'Modern responsive portals',
      'Dashboards',
      'Administration systems',
      'Customer-facing applications',
      'Real-time interfaces',
      'PWA capability',
    ],
    icon: Layers3,
    accent: 'blue',
  },
  {
    slug: 'mobile-applications',
    number: '03',
    title: 'Mobile app development',
    summary: 'Practical mobile tools for customers, drivers, employees, and field teams.',
    description:
      'Good field software respects the environment it is used in. We build mobile experiences for Android, iOS, and cross-platform delivery, with the device capabilities and connectivity realities of your teams considered from the start.',
    capabilities: [
      'Android',
      'iOS',
      'Cross-platform solutions',
      'Offline-first applications',
      'Camera and scanner integration',
      'GPS and location features',
      'Push notifications',
      'Field-worker applications',
    ],
    icon: Smartphone,
    accent: 'violet',
  },
  {
    slug: 'backend-api-engineering',
    number: '04',
    title: 'Backend & API engineering',
    summary: 'Reliable foundations for data, integrations, permissions, and real-time operations.',
    description:
      'We engineer the systems behind the interface: the services that protect data, coordinate work, connect platforms, and keep important processes moving. The result is a dependable foundation that supports today’s use case without closing off tomorrow’s options.',
    capabilities: [
      'REST APIs',
      'Integration services',
      'Distributed systems',
      'Authentication and authorisation',
      'Background processing',
      'Event-driven systems',
      'Caching',
      'Real-time communication',
      'Database design',
    ],
    icon: CloudCog,
    accent: 'blue',
  },
  {
    slug: 'systems-integration',
    number: '05',
    title: 'Systems integration',
    summary: 'Connect the systems, devices, and providers your business already depends on.',
    description:
      'Integration is more than moving data from one place to another. We map the handoffs, ownership, timing, and failure cases so connected systems create a more dependable operation instead of a more complicated one.',
    capabilities: [
      'ERP systems',
      'Accounting platforms',
      'Payment providers',
      'External APIs',
      'IoT and devices',
      'Industrial equipment',
      'Communication platforms',
    ],
    icon: PlugZap,
    accent: 'crimson',
  },
  {
    slug: 'business-process-automation',
    number: '06',
    title: 'Business process automation',
    summary: 'Remove repetitive handoffs and give teams a live view of work in motion.',
    description:
      'We find the manual steps that create delay, inconsistency, and duplicate capture, then design a more dependable workflow around them. Automation should make work more visible and accountable—not make the system harder to understand.',
    capabilities: [
      'Rules and triggers',
      'Approvals',
      'Validation flows',
      'Notifications',
      'Scheduled processing',
      'Audit trails',
      'Live operational dashboards',
    ],
    icon: Workflow,
    accent: 'violet',
  },
  {
    slug: 'mvp-saas-product-development',
    number: '07',
    title: 'MVP & SaaS product development',
    summary: 'Move a product idea from a strong hypothesis to a dependable production platform.',
    description:
      'We help founders and established businesses turn an idea into something people can use and learn from. The work is staged so each decision earns the next one: validate the problem, shape the smallest useful product, learn from a pilot, and build with production in mind.',
    capabilities: [
      'Product discovery',
      'Validation and prioritisation',
      'SaaS platform foundations',
      'MVP delivery',
      'Pilot preparation',
      'Production readiness',
      'Continuous improvement',
    ],
    icon: Gauge,
    accent: 'blue',
  },
];

export const solutionDetails: SolutionDetail[] = [
  {
    slug: 'logistics-transport',
    number: '01',
    title: 'Logistics & transport',
    summary: 'Make every load, handoff, and delivery easier to see and manage.',
    description:
      'Transport operations have to coordinate moving assets, changing conditions, paperwork, and customer expectations at the same time. We design systems that give dispatchers, drivers, operators, and customers a shared operational picture.',
    problems: [
      'Load visibility',
      'Driver coordination',
      'Delivery status',
      'POD and manual paperwork',
      'Weighbridge data',
      'Reconciliation',
      'Customer visibility',
      'Dispatch management',
    ],
    highlights: [
      'Transport management platforms',
      'Driver mobile apps',
      'Operations dashboards',
      'Automated document workflows',
      'Real-time tracking',
      'Integration with existing systems',
    ],
    solutions: [
      'Connect the load lifecycle from collection through delivery',
      'Give field teams the right mobile workflow for the moment',
      'Reduce duplicate capture between dispatch, drivers, and customers',
      'Surface exceptions early so teams can act before they become delays',
    ],
    icon: MapPinned,
    accent: 'crimson',
  },
  {
    slug: 'mining-industrial-operations',
    number: '02',
    title: 'Mining & industrial operations',
    summary:
      'Connect distributed sites, equipment, and evidence into a dependable operating layer.',
    description:
      'Industrial environments do not always have perfect connectivity, standardised equipment, or one neat source of truth. We design for the conditions on the ground, then connect site-level activity to the central platform the wider operation needs.',
    problems: [
      'Distributed sites',
      'Inconsistent connectivity',
      'Equipment integration',
      'Load movement',
      'Entry and exit weights',
      'Operational evidence',
      'Reconciliation',
    ],
    highlights: [
      'Offline-first systems',
      'Edge software',
      'Hardware integration',
      'Central operational platforms',
    ],
    solutions: [
      'Capture work reliably when connectivity is intermittent',
      'Integrate with equipment and site systems where the data originates',
      'Keep an auditable operational record across sites and teams',
      'Reconcile movement, weights, and evidence in one central view',
    ],
    icon: Boxes,
    accent: 'blue',
  },
  {
    slug: 'business-operations',
    number: '03',
    title: 'Business operations',
    summary: 'Replace fragile workarounds with business systems people can actually rely on.',
    description:
      'The operational core of a business is often held together by spreadsheets, inboxes, and people who know the workaround. We turn those informal systems into visible, repeatable workflows without losing the practical knowledge that made them work in the first place.',
    problems: [
      'Spreadsheet dependency',
      'Repetitive data entry',
      'Duplicated systems',
      'Poor reporting',
      'Approval bottlenecks',
    ],
    highlights: [
      'Internal business systems',
      'Workflow tools',
      'Administration portals',
      'Reporting platforms',
    ],
    solutions: [
      'Create one dependable place for shared operational data',
      'Make approvals and ownership visible',
      'Turn repeatable work into guided workflows',
      'Give leaders reporting that reflects the operation, not a manual snapshot',
    ],
    icon: Braces,
    accent: 'violet',
  },
  {
    slug: 'financial-workflows',
    number: '04',
    title: 'Financial workflows',
    summary:
      'Build secure, traceable workflows around approvals, transactions, and reconciliation.',
    description:
      'We help organisations improve the software around financial work without positioning CrimsonSky as a financial institution. The focus is on the workflow layer: who can do what, what needs to be evidenced, and how movement through the process can be understood.',
    problems: [
      'Approval bottlenecks',
      'Manual reconciliation',
      'Low transaction visibility',
      'Disconnected systems',
      'Audit preparation',
    ],
    highlights: [
      'Secure workflows',
      'Approvals',
      'Audit trails',
      'Reconciliation',
      'Integration',
      'Transaction visibility',
    ],
    solutions: [
      'Design clear permissions and approval paths',
      'Keep a useful history of decisions and changes',
      'Connect financial workflows to existing systems and providers',
      'Make exceptions and reconciliation work visible to the right people',
    ],
    icon: DatabaseZap,
    accent: 'crimson',
  },
  {
    slug: 'digital-transformation',
    number: '05',
    title: 'Digital transformation',
    summary: 'Modernise incrementally, with a clear next step and a useful outcome at each stage.',
    description:
      'Digital transformation does not have to mean a risky all-at-once replacement. We work with the systems and constraints already in place, identify where change will have the most leverage, and deliver in stages that the business can understand and absorb.',
    problems: [
      'Legacy processes',
      'Manual workarounds',
      'Brittle integrations',
      'Unclear priorities',
      'Risky all-at-once change',
    ],
    highlights: [
      'Process discovery',
      'Incremental modernisation',
      'System integration',
      'Staged rollout',
      'Continuous improvement',
    ],
    solutions: [
      'Map the current process before choosing the technology',
      'Prioritise the change that removes the most friction',
      'Introduce new capability alongside what must remain',
      'Build a roadmap that earns confidence through useful delivery',
    ],
    icon: Workflow,
    accent: 'blue',
  },
];

export const products: Product[] = [
  {
    slug: 'loadix',
    name: 'LoadIx',
    category: 'Logistics operations',
    summary: 'Turn every load into a traceable operational record.',
    description:
      'LoadIx brings planning, vehicles, drivers, collection events, weighbridge data, delivery evidence, reconciliation, and operational documents together in one platform.',
    status: 'Pilot ready',
    icon: Gauge,
    accent: 'crimson',
    capabilities: [
      'Load lifecycle',
      'Driver operations',
      'Weight reconciliation',
      'Proof of delivery',
      'Offline workflows',
      'Operational visibility',
    ],
  },
];

export const solutionSignals = [
  { label: 'Logistics & transport', icon: MapPinned },
  { label: 'Mining & industrial operations', icon: Boxes },
  { label: 'Financial workflows', icon: DatabaseZap },
  { label: 'Internal business systems', icon: Braces },
  { label: 'Real-time operations', icon: Network },
  { label: 'Workflow automation', icon: Cog },
];

export const trustCapabilities = [
  { label: 'Custom software', icon: Code2 },
  { label: 'SaaS platforms', icon: Layers3 },
  { label: 'API integration', icon: PlugZap },
  { label: 'Cloud systems', icon: CloudCog },
  { label: 'Mobile apps', icon: Smartphone },
];

export const processSteps = [
  'Discovery',
  'System design',
  'UX',
  'Development',
  'Integration',
  'Deployment',
  'Improvement',
];

export const foundationPrinciples = [
  { title: 'Clarity over complexity', icon: GitBranch },
  { title: 'Built for the real world', icon: Cog },
  { title: 'A foundation that can grow', icon: Layers3 },
];

export const aboutThinking = [
  {
    number: '01',
    title: 'Understand the operation',
    description:
      'Good software begins with understanding the actual workflow—not the idealised version of it.',
    icon: Search,
  },
  {
    number: '02',
    title: 'Design the system',
    description:
      'System design should support the business rather than become another constraint to work around.',
    icon: Layers3,
  },
  {
    number: '03',
    title: 'Build deliberately',
    description:
      'Quality, maintainability, and security are part of the product from the first useful release.',
    icon: Code2,
  },
  {
    number: '04',
    title: 'Improve continuously',
    description:
      'Software should evolve as the business evolves, with each improvement grounded in what people learn by using it.',
    icon: RefreshCw,
  },
];

export const engineeringPrinciples = [
  {
    title: 'Pragmatism over hype',
    description: 'Choose the approach that makes the next useful outcome possible.',
    icon: Cog,
  },
  {
    title: 'Security by design',
    description:
      'Trust, access, and data protection belong in the foundation—not in a last-minute checklist.',
    icon: ShieldCheck,
  },
  {
    title: 'Automation over repetition',
    description: 'Let systems handle the repeatable work so people can focus on decisions.',
    icon: Workflow,
  },
  {
    title: 'Data over assumptions',
    description:
      'Make the operation visible enough that better decisions have something solid to stand on.',
    icon: DatabaseZap,
  },
  {
    title: 'Simple where possible',
    description: 'Fewer moving parts are easier to understand, operate, and improve.',
    icon: GitBranch,
  },
  {
    title: 'Scalable where necessary',
    description: 'Plan for real growth without building complexity the business does not need yet.',
    icon: Network,
  },
  {
    title: 'Build for real users',
    description:
      'The people living with the workflow are the ones who define whether software works.',
    icon: Users,
  },
];

export const technologyGroups = [
  {
    label: 'Backend',
    description: 'Reliable services and domain logic.',
    items: ['C#', '.NET'],
    icon: CloudCog,
  },
  {
    label: 'Frontend',
    description: 'Clear interfaces for useful work.',
    items: ['React', 'TypeScript'],
    icon: Code2,
  },
  {
    label: 'Platforms',
    description: 'The surfaces the business actually uses.',
    items: ['Web', 'Mobile', 'Cloud', 'APIs'],
    icon: Layers3,
  },
  {
    label: 'Infrastructure',
    description: 'The foundations that keep systems dependable.',
    items: ['SQL', 'Docker', 'Message queues', 'Caching', 'Object storage'],
    icon: Network,
  },
];
